import React, { useEffect, useState } from 'react';
import { ClipboardList, Loader2, X, Check, Phone, Mail, Calendar, AlertCircle, Clock } from 'lucide-react';

// Review panel for AI-extracted follow-up tasks. The model proposes,
// the human commits. Each task in the panel has a checkbox so the
// operator can pick which actually get created — defaults to all
// checked because the model already filters internally, but the
// "Create selected" CTA is always explicit.
//
// Props:
//   isOpen, isLoading, result, error, onCreate, onClose
//
// `result` shape: { tasks: [{title, description, type, priority, dueOffsetHours, rationale}], summary }
// `onCreate(selectedTasks)` is called when the operator commits — the parent
// is responsible for POSTing to /api/tasks and closing the panel.

const TYPE_META = {
  call:     { label: 'Call',     icon: Phone,    tint: 'bg-emerald-100 text-emerald-800' },
  email:    { label: 'Email',    icon: Mail,     tint: 'bg-blue-100 text-blue-800' },
  meeting:  { label: 'Meeting',  icon: Calendar, tint: 'bg-purple-100 text-purple-800' },
  deadline: { label: 'Deadline', icon: AlertCircle, tint: 'bg-amber-100 text-amber-800' },
  todo:     { label: 'To-do',    icon: ClipboardList, tint: 'bg-slate-100 text-slate-700' },
};

const PRIORITY_META = {
  high:   { label: 'High',   tint: 'bg-rose-100 text-rose-800' },
  normal: { label: 'Normal', tint: 'bg-gray-100 text-gray-700' },
  low:    { label: 'Low',    tint: 'bg-gray-50 text-gray-500' },
};

function dueLabel(hours) {
  if (hours == null) return 'No due date';
  if (hours <= 4) return 'Later today';
  if (hours <= 24) return 'Tomorrow';
  if (hours <= 72) return `In ${Math.round(hours / 24)} days`;
  if (hours <= 168) return 'Next week';
  return `In ${Math.round(hours / 24)} days`;
}

const AISuggestTasksPanel = ({ isOpen, isLoading, result, error, onCreate, onClose }) => {
  // Per-task checked state. Defaults to all tasks selected each time
  // the model returns a fresh result — the user wanted a one-tap
  // "create everything" flow when the suggestions look right.
  const [checked, setChecked] = useState({});
  useEffect(() => {
    if (result?.tasks) {
      const next = {};
      result.tasks.forEach((_, i) => { next[i] = true; });
      setChecked(next);
    }
  }, [result]);

  if (!isOpen) return null;

  const tasks = result?.tasks || [];
  const selectedCount = Object.values(checked).filter(Boolean).length;

  const commit = () => {
    if (!result) return;
    const picked = tasks.filter((_, i) => checked[i]);
    if (picked.length === 0) return;
    onCreate(picked);
  };

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 mx-2 z-30">
      <div className="bg-white border border-indigo-200 shadow-xl rounded-lg overflow-hidden max-h-[480px] flex flex-col">
        <div className="px-4 py-2 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <ClipboardList size={14} />
            AI follow-up tasks
            {isLoading && <Loader2 size={12} className="animate-spin text-indigo-500 ml-1" />}
            {!isLoading && tasks.length > 0 && (
              <span className="text-xs font-normal text-indigo-700 ml-1">{tasks.length} suggested</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-700 rounded"
            aria-label="Close task suggestions"
          >
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-2">
          {isLoading && (
            <div className="text-sm text-gray-500 py-6 text-center">Analyzing the conversation…</div>
          )}
          {error && !isLoading && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
          )}
          {!isLoading && !error && tasks.length === 0 && (
            <div className="text-sm text-gray-500 py-6 text-center">
              No open follow-up actions detected in this conversation.
            </div>
          )}
          {!isLoading && tasks.map((t, i) => {
            const meta = TYPE_META[t.type] || TYPE_META.todo;
            const Icon = meta.icon;
            const prio = PRIORITY_META[t.priority] || PRIORITY_META.normal;
            return (
              <label
                key={i}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                  checked[i] ? 'border-indigo-300 bg-indigo-50/40' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!checked[i]}
                  onChange={(e) => setChecked((p) => ({ ...p, [i]: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 truncate">{t.title}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${meta.tint}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${prio.tint}`}>{prio.label}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                      <Clock size={10} /> {dueLabel(t.dueOffsetHours)}
                    </span>
                  </div>
                  {t.description && (
                    <p className="text-xs text-gray-700 mt-1 leading-snug">{t.description}</p>
                  )}
                  {t.rationale && (
                    <p className="text-[11px] text-gray-500 mt-1 italic">Why: {t.rationale}</p>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {!isLoading && tasks.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-600">
              {selectedCount} of {tasks.length} selected
            </span>
            <button
              onClick={commit}
              disabled={selectedCount === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={12} /> Create {selectedCount > 0 ? `${selectedCount} ` : ''}task{selectedCount === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISuggestTasksPanel;
